/** @format */

import { Localized } from "@fluent/react";
import { useLocalStorage, useSetState } from "@mantine/hooks";
import LoadPage from "Components/loadPage/loadPage";
import { DB_RESPONSE_OK } from "constants/dbStatuses";
import { getBoothDataFromDatabase } from "indexedDb/operations";
import {
	getEventsRecordsFromDatabase,
	getScanModeOptionsFromIndexedDb,
	getScannerConfigurationFromDatabase,
	getScanSettingsFromDatabase,
	getUserSettingsFromDatabase,
	modifyScanModeOptionsInIndexedDb,
	modifyScanSettingsRecordInDatabase,
	modifyUserSettingRecordInDatabase,
	setConfigurationToDatabase,
} from "indexedDb/operations";
import { useCallback, useEffect, useState, useContext, createContext, useMemo } from "react";
import { LocaleContext } from "util/localizationProvider";
import {
	initialScannerConfiguration,
	initialScanSettings,
	initialUserSettings,
	initialScanMode,
	boothSettings,
} from "constants/initialSettings";
import * as Sentry from "@sentry/react";


export const ConfigurationsContext = createContext({});

async function handleWakelock() {
	if (navigator.wakeLock) {
		await navigator.wakeLock.request();
	} else {
		//await navigator.wakeLock.release();
	}
}

const ConfigurationsContextProvider = ({ children }) => {
	const { setLanguage } = useContext(LocaleContext);

	const [initialized, setInitialized] = useState(false);

	// Scanner configuration
	const [scannerConfiguration, setScannerConfiguration] = useSetState(initialScannerConfiguration);
	const [events, setEvents] = useState([]);
	const [boothOptions, setBoothOptions] = useState({});

	// Scanner settings
	const [scanSettings, setScanSettings] = useState(initialScanSettings);

	// User settings
	const [userSettings, setUserSettings] = useState(initialUserSettings);

	// Scan options, which section / entry your scanning..
	const [scanMode, setScanMode] = useState(initialScanMode);

	const persistScannerConfigurations = useCallback(async (configuration, events, booth) => {
		const { status, data } = await setConfigurationToDatabase({
			configuration: configuration,
			events: events,
			booth: booth,
		});

		if (status === DB_RESPONSE_OK) {
			setScannerConfiguration(conf => ({ ...conf, ...data.configuration }));
			setEvents(data.events);
			setBoothOptions(data.boothOptions);
		}
		return { status, data };
	}, []);

	// Device settings

	// Scanning mode modifications
	const modifyScanMode = useCallback(async data => {
		const { status, data: dbData } = await modifyScanModeOptionsInIndexedDb(data);
		if (status === DB_RESPONSE_OK) {
			setScanMode(dbData);
		}
		return { status, data };
	}, []);

	// Generic user specific scanning settings
	const modifyScanSettings = useCallback(async (key, value) => {
		const { status, data } = await modifyScanSettingsRecordInDatabase(key, value);

		if (status === DB_RESPONSE_OK) {
			setScanSettings(data);
		}
		return { status, data };
	}, []);

	// User specific usability settings
	const modifyUserSetting = useCallback(async (key, value) => {
		const { status, data } = await modifyUserSettingRecordInDatabase(key, value);
		if (status === DB_RESPONSE_OK) {
			setUserSettings(data);
			if (key === "language") {
				setLanguage(data.language);
			}
		}
	}, []);

	const initializeConfiguration = useCallback(async () => {
		try {
			await Promise.all([
				getUserSettingsFromDatabase(initialUserSettings).then(userSettingsResponse => {
					if (userSettingsResponse.status === DB_RESPONSE_OK) {
						setUserSettings(s => ({ ...s, ...userSettingsResponse.data }));
					}
				}),
				getScanSettingsFromDatabase(initialScanSettings).then(scanSettingsResponse => {
					if (scanSettingsResponse.status === DB_RESPONSE_OK) {
						setScanSettings(s => ({ ...s, ...scanSettingsResponse.data }));
					}
				}),
				getScanModeOptionsFromIndexedDb(initialScanMode).then(scanModeSettingsResponse => {
					if (scanModeSettingsResponse.status === DB_RESPONSE_OK) {
						setScanMode(s => ({ ...s, ...scanModeSettingsResponse.data }));
					}
				}),
				// Scanner configuration specific data
				getScannerConfigurationFromDatabase().then(async scanConfResponse => {
					if (scanConfResponse.status === DB_RESPONSE_OK) {
						setScannerConfiguration(scanConfResponse.data);
						Sentry.setContext("config", scanConfResponse.data);
						return await Promise.all([
							getEventsRecordsFromDatabase().then(eventsResponse => {
								if (eventsResponse.status === DB_RESPONSE_OK) {
									setEvents(eventsResponse.data);
								}
							}),
							getBoothDataFromDatabase(scanConfResponse.data.apikey).then(boothResponse => {
								if (boothResponse.status === DB_RESPONSE_OK) {
									setBoothOptions(boothResponse.data);
								}
							}),
						]);
					}
				}).catch(err => {
					// it's okay if we're missing scanner config
				}),
			]);
		} finally {
			Sentry.addBreadcrumb({message: "Config initialized"})
			setInitialized(true);
		}
	}, []);

	useEffect(() => {
		initializeConfiguration();
	}, [initializeConfiguration]);

	const ctx = useMemo(
		() => ({
			scannerConfiguration,
			persistScannerConfigurations,
			userSettings,
			setUserSettings,
			modifyUserSetting,
			scanSettings,
			modifyScanSettings,
			initialized,
			boothMode: boothOptions.booth_mode_only || false,
			allowedBoothNames: boothOptions.allowed_booth_names || [],
			events,
			scanMode,
			modifyScanMode,
		}),
		[
			scannerConfiguration,
			persistScannerConfigurations,
			userSettings,
			modifyUserSetting,
			scanSettings,
			modifyScanSettings,
			initialized,
			boothOptions,
			events,
			scanMode,
			modifyScanMode,
		],
	);

	useEffect(() => {
		if (userSettings.wakelock) {
			handleWakelock();
		}
	}, [userSettings.wakelock]);

	if (!initialized) {
		return <LoadPage text={<Localized id="app-loading" />} />;
	}

	return <ConfigurationsContext.Provider value={ctx}>{children || null}</ConfigurationsContext.Provider>;
};

export default ConfigurationsContextProvider;
